import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  const lastUpdated = 'May 27, 2026';

  const apps = [
    { name: 'Jumpy Neon: Retro Arcade Game', pkg: 'com.rivenbyte.jumpy.neon', category: 'Endless Runner' },
    { name: 'Neon Dash', pkg: 'com.rivenbyte.neondash', category: 'Endless Runner' },
    { name: 'Neon Solitaire', pkg: 'com.rivenbyte.neonsolitaire', category: 'Card Puzzle' },
    { name: 'Neon Crossword', pkg: 'com.rivenbyte.neoncrossword', category: 'Word Game' },
    { name: 'Block Drop', pkg: 'com.rivenbyte.tetris', category: 'Puzzle' },
    { name: 'Drop Four', pkg: 'com.rivenbyte.dropfour', category: 'Board Game' },
    { name: 'Game Vault', pkg: 'com.rivenbyte.gamevault', category: 'Mini Games Collection' },
  ];

  return (
    <div className="min-h-screen bg-black text-gray-300">
      {/* Header */}
      <div className="bg-gradient-to-b from-primary-500/10 to-transparent pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="text-primary-400 hover:text-primary-300 text-sm mb-4 inline-flex items-center gap-2">
            ← Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-gaming font-bold text-white mt-4">
            Privacy Policy
          </h1>
          <p className="text-gray-500 mt-4">Last updated: {lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 pb-24">
        <div className="prose prose-invert prose-lg max-w-none">

          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
            <p>
              Riven Byte Studios ("we," "our," or "us") is a game development studio based in
              Dar es Salaam, Tanzania. This Privacy Policy describes how we collect, use, and
              protect information when you use any of our mobile games and applications
              (collectively, "the Apps").
            </p>
            <p className="mt-3">
              By downloading or using any of our Apps, you agree to this Privacy Policy.
              If you do not agree, please do not use our Apps.
            </p>
          </section>

          {/* Covered Apps */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">2. Covered Applications</h2>
            <p className="mb-4">This policy applies to all games published by Riven Byte Studios, including:</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {apps.map((app) => (
                <div key={app.pkg} className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <h4 className="text-white font-semibold text-sm">{app.name}</h4>
                  <p className="text-gray-500 text-xs mt-1">{app.pkg}</p>
                  <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-400 border border-primary-500/20">
                    {app.category}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Data Collection */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">3. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-white mt-6 mb-3">3.1 Information We Do NOT Collect</h3>
            <p>Our games are designed to be played offline and do not require any account creation or login. We do not collect:</p>
            <ul className="mt-3 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-1">✓</span>
                <span>Personal information (name, email, phone number, address)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-1">✓</span>
                <span>Location data or GPS coordinates</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-1">✓</span>
                <span>Contacts, photos, camera, or microphone data</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-1">✓</span>
                <span>Financial or payment information</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-1">✓</span>
                <span>User-generated content or messages</span>
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-white mt-8 mb-3">3.2 Information Collected by Third-Party Services</h3>
            <p>Our Apps may use third-party services that collect data independently:</p>

            <div className="bg-white/5 rounded-xl p-6 mt-4 border border-white/10">
              <h4 className="text-white font-bold mb-2">Google AdMob</h4>
              <p className="text-sm">
                Some of our Apps display advertisements through Google AdMob. AdMob may collect
                device identifiers (Advertising ID), approximate location, device type, operating
                system version, and interaction data for the purpose of serving relevant ads.
                Google's data practices are governed by{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer"
                   className="text-primary-400 hover:text-primary-300 underline">
                  Google's Privacy Policy
                </a>.
              </p>
            </div>

            <div className="bg-white/5 rounded-xl p-6 mt-3 border border-white/10">
              <h4 className="text-white font-bold mb-2">Google Play Services</h4>
              <p className="text-sm">
                Our Apps are distributed through Google Play, which may collect crash reports,
                app usage analytics, and device information. This is governed by{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer"
                   className="text-primary-400 hover:text-primary-300 underline">
                  Google's Privacy Policy
                </a>.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-white mt-8 mb-3">3.3 Local Storage</h3>
            <p>
              Our Apps store game progress, high scores, and user preferences locally on your
              device using SharedPreferences and SQLite databases. This data never leaves your
              device and is not transmitted to any server.
            </p>
          </section>

          {/* Ads */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">4. Advertising</h2>
            <p>
              Some of our Apps display advertisements through Google AdMob, including banner ads,
              interstitial ads, and rewarded video ads. These ads help us keep our games free
              for all users.
            </p>
            <p className="mt-3">
              You may opt out of personalized advertising by adjusting your device settings:
            </p>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <strong className="text-white">Android:</strong> Settings → Google → Ads → Opt out of Ads Personalization
              </li>
              <li>
                <strong className="text-white">iOS:</strong> Settings → Privacy → Advertising → Limit Ad Tracking
              </li>
            </ul>
          </section>

          {/* Children */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">5. Children's Privacy</h2>
            <p>
              Our Apps are not directed at children under the age of 13 (or the applicable age
              in your jurisdiction). We do not knowingly collect personal information from
              children. If you believe a child has provided personal information to us through
              a third-party service, please contact us and we will take steps to address it.
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">6. Data Retention & Deletion</h2>
            <p>
              Since we do not collect personal data, there is no data to retain on our servers.
              All game data is stored locally on your device. You can delete all app data by:
            </p>
            <ul className="mt-3 space-y-2 text-sm">
              <li>Uninstalling the App from your device</li>
              <li>Clearing the App's data through your device settings</li>
              <li>Using any in-app "Reset" or "Clear Data" feature if available</li>
            </ul>
          </section>

          {/* Security */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">7. Data Security</h2>
            <p>
              We take reasonable measures to protect the information associated with our Apps.
              However, no method of electronic transmission or storage is 100% secure. While we
              strive to protect your data, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Changes */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">8. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on
              this page with an updated "Last updated" date. Continued use of our Apps after
              any changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Per-app sections */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">9. App-Specific Details</h2>
            <div className="space-y-4">
              {apps.map((app) => (
                <div key={app.pkg} id={app.pkg.replace(/\./g, '-')} className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <h3 className="text-white font-bold">{app.name}</h3>
                  <p className="text-gray-500 text-xs mb-2">{app.pkg}</p>
                  <p className="text-sm">
                    {app.name} is a free-to-play {app.category.toLowerCase()} game. It stores
                    game progress and scores locally on your device. It displays advertisements
                    through Google AdMob. No personal information is collected, and no account
                    or login is required to play.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className="px-2 py-1 rounded bg-green-500/10 text-green-400 border border-green-500/20">No login required</span>
                    <span className="px-2 py-1 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">Offline capable</span>
                    <span className="px-2 py-1 rounded bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">Contains ads</span>
                    <span className="px-2 py-1 rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">Local storage only</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">10. Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy, please contact us:
            </p>
            <div className="bg-white/5 rounded-xl p-6 mt-4 border border-white/10">
              <p className="text-white font-semibold">Riven Byte Studios</p>
              <p className="text-sm mt-2">Email: <a href="mailto:hello@rivenbyte.com" className="text-primary-400 hover:text-primary-300">hello@rivenbyte.com</a></p>
              <p className="text-sm">Location: Dar es Salaam, Tanzania</p>
              <p className="text-sm">Website: <a href="https://rivenbyte.com" className="text-primary-400 hover:text-primary-300">rivenbyte.com</a></p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
